import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import {
  replaceEmoneyRequestApi,
  ResendOtpForReplaceEMoney,
  submitReplaceEMoneyOtp,
  viewEarningWalletApi,
} from "../../apis";
import ReplaceEMoneyRequestsHistory from "./ReplaceEMoneyRequestsHistory";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../Components/UI/PageTitle";
import RowLoader from "../../Components/UI/RowLoader";
import { isArray } from "lodash";

const initialValues = {
  emoney: "",
  notes: "",
  confirm: false,
};
let countdown = 60;
let timerInterval;

const validationSchema = Yup.object().shape({
  emoney: Yup.number().required("Required").positive("Must be greater than 0"),
  notes: Yup.string().required("Required"),
  confirm: Yup.boolean().oneOf([true], "You must confirm the replace"),
});

const ReplaceEmoney = () => {
  const [userEarningWallet, setUserEarningWallet] = useState();
  const navigate = useNavigate();
  const resendOtp = () => {
    ResendOtpForReplaceEMoney()
      .then((response) => {
        if (response.status === true) {
          showOtpModal(); // Re-open the modal
        }
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Resend Failed",
          text:
            error?.response?.data?.message?.[0] ||
            "Unable to resend OTP. Please try again.",
        });
      });
  };
  const showOtpModal = (resetForm) => {
    countdown = 60;

    Swal.fire({
      title: "Enter OTP",
      html: `
          <p style="margin-bottom: 10px; color: #4B5563; font-size: 14px;">
            The OTP has been sent to your email.
            <i class="material-icons">email</i>
          </p>
          <input id="otp-input" class="swal2-input" placeholder="Enter the 6-digit code" maxlength="6" />
          <p id="resend-timer" style="margin-top: 10px;">Resend OTP in <b>${countdown}</b>s</p>
        `,
      showCancelButton: true,
      confirmButtonText: "Verify",
      didOpen: () => {
        const content = Swal.getHtmlContainer();
        const $timer = content.querySelector("#resend-timer");

        timerInterval = setInterval(() => {
          countdown--;
          if (countdown <= 0) {
            clearInterval(timerInterval);
            $timer.innerHTML = `
                <button id="resend-btn" class="tw-bg-white tw-text-gray-800 tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-1 tw-text-sm tw-inline-flex tw-items-center">
                  <i class="material-icons tw-text-base tw-mr-1">refresh</i> Resend
                </button>
              `;
            document
              .getElementById("resend-btn")
              .addEventListener("click", () => {
                countdown = 60;
                resendOtp();
              });
          } else {
            $timer.innerHTML = `Resend OTP in <b>${countdown}</b>s`;
          }
        }, 1000);
      },
      preConfirm: () => {
        const otp = document.getElementById("otp-input").value;
        if (!otp) {
          Swal.showValidationMessage("OTP is required");
        }
        return otp;
      },
      willClose: () => {
        clearInterval(timerInterval);
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const enteredOTP = result.value;
        submitReplaceEMoneyOtp(enteredOTP)
          .then((response) => {
            Swal.fire({
              title: "Success",
              text: "The request has been sent successfully",
              icon: "success",
            }).then(() => {
              navigate(0);
              resetForm();
            });
          })
          .catch((error) => {
            Swal.fire({
              title: "Error",
              text: error?.response?.data?.message?.[0] || "Wrong OTP",
              icon: "error",
            });
          });
      }
    });
  };
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const formData = {
      emoney: values.emoney,
      notes: values.notes,
      confirm: values.confirm ? 1 : 0,
    };

    try {
      await replaceEmoneyRequestApi(formData);
      showOtpModal(resetForm);
    } catch (error) {
      let errorMessage = isArray(error?.response?.data?.message)
        ? error?.response?.data?.message?.[0]
        : error?.message;

      Swal.fire({
        title: "Error",
        text: `${errorMessage}`,
        icon: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchEarningWallet = async () => {
      try {
        const response = await viewEarningWalletApi();
        setUserEarningWallet(response.data.data);
      } catch (error) {
        console.error("Error fetching payment methods:", error);
      }
    };

    fetchEarningWallet();
  }, []);

  return (
    <>
      <PageTitle title="Replace E-Money" />

      <div className="tw-flex-col gap-6">
        <div className="card">
          <div className="card-header d-flex max-sm:!tw-flex-col align-items-center justify-content-between">
            <h4 className="card-title mb-0">Replace E-Money</h4>
            <h4 className="tw-capitalize tw-text-lg !tw-mb-0">
              current earning wallet{" "}
              <span className="tw-text-primary tw-font-medium ">
                {userEarningWallet} e.m
              </span>
            </h4>
          </div>
          <div className="card-body">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, setFieldValue, values, isSubmitting }) => (
                <Form>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label
                        className="form-label required-label"
                        htmlFor="emoney"
                      >
                        E-Money
                      </label>
                      <Field
                        type="number"
                        name="emoney"
                        className={`form-control ${
                          touched.emoney && errors.emoney
                            ? "custom-invalid-input"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="emoney"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label
                        className="form-label required-label"
                        htmlFor="notes"
                      >
                        Notes
                      </label>
                      <Field
                        as="textarea"
                        placeholder="Kindly provide details on how you'd like to receive the money"
                        name="notes"
                        className={`form-control ${
                          touched.notes && errors.notes
                            ? "custom-invalid-input"
                            : ""
                        }`}
                        rows="4"
                      />
                      <ErrorMessage
                        name="notes"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="form-check mb-3 tw-flex tw-items-end tw-w-full">
                      <input
                        type="checkbox"
                        className={`form-check-input tw-h-5 tw-w-5 ${
                          touched.confirm && errors.confirm ? "is-invalid" : ""
                        }`}
                        id="confirmCheckbox"
                        name="confirm"
                        checked={values.confirm}
                        onChange={(e) =>
                          setFieldValue("confirm", e.target.checked)
                        }
                      />
                      <label
                        className="form-check-label !tw-m-0 tw-ps-4 tw-w-full"
                        htmlFor="confirmCheckbox"
                      >
                        I confirm that the replace process is correct, and I
                        can't cancel it.
                      </label>
                      <ErrorMessage
                        name="confirm"
                        component="div"
                        className="invalid-feedback tw-block"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary ml-auto mt-5 !tw-w-[100px]"
                  >
                    {isSubmitting ? (
                      <RowLoader paddingStyle="tw-py-0" />
                    ) : (
                      "Submit"
                    )}
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        </div>
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h4 className="card-title mb-0">
              Replace E-Money Requests History
            </h4>
          </div>
          <div className="card-body">
            <ReplaceEMoneyRequestsHistory />
          </div>
        </div>
      </div>
    </>
  );
};

export default ReplaceEmoney;
